import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Plus, Loader2 } from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { resolveAssetUrl } from "@/lib/assetUrl";

// Fields the realtor owns on their own listing. Kept in sync with the
// backend `CategoryProfilePatch` model — anything you add here must
// also be accepted by `PATCH /businesses/{id}/category-profile`.
const MAX_SPECIALTIES = 3;

/**
 * Dashboard editor a signed-in realtor uses to personalise the hero of
 * their own listing (tagline, up to three specialties, hero portrait).
 * Rendered inside DashboardPage only when the currently-selected business
 * has `category === "Real Estate"`.
 */
export default function RealtorOwnerEditor({ business, onSaved }) {
  const [tagline, setTagline] = useState("");
  const [specialties, setSpecialties] = useState(["", "", ""]);
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Rehydrate form whenever the selected business changes so the owner
  // is always editing the fields for the business they picked.
  useEffect(() => {
    if (!business) return;
    setTagline(business.tagline || "");
    const specs = Array.isArray(business.specialties) ? business.specialties : [];
    setSpecialties([
      specs[0] || "",
      specs[1] || "",
      specs[2] || "",
    ]);
    setImageUrl(business.image_url || "");
  }, [business]);

  const updateSpecialty = (idx, value) => {
    setSpecialties((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const uploadPortrait = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Hero photo must be under 5 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("image_type", "business");
      const r = await axios.post(`${API}/upload/image`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.url;
      if (!url) throw new Error("no url returned");
      setImageUrl(url);
      toast.success("Photo uploaded — click Save to publish it.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!business) return;
    setSaving(true);
    try {
      const payload = {
        tagline: tagline.trim(),
        specialties: specialties.map((s) => s.trim()).filter(Boolean).slice(0, MAX_SPECIALTIES),
        image_url: imageUrl || "",
      };
      const r = await axios.patch(
        `${API}/businesses/${business.business_id}/category-profile`,
        payload,
        { withCredentials: true },
      );
      toast.success("Realtor profile updated.");
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!business || business.category !== "Real Estate") return null;

  return (
    <section
      className="rounded-2xl border border-tbc-gold/30 bg-white p-6 shadow-sm"
      data-testid="realtor-owner-editor"
    >
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl text-tbc-navy sm:text-2xl">
            Realtor Profile
          </h2>
          <p className="mt-1 font-body text-sm text-gray-600">
            Personalise the hero card that visitors see when they land on{" "}
            <span className="font-medium text-tbc-navy">{business.name}</span>.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-full bg-tbc-navy px-5 text-white hover:bg-tbc-navy/90"
          data-testid="realtor-owner-save-btn"
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* LEFT column — text fields */}
        <div className="space-y-5">
          <div>
            <label className="mb-1 block font-body text-sm font-medium text-tbc-navy">
              Tagline
            </label>
            <Textarea
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder="Your Trusted Partner in Buying, Selling & Investing"
              rows={2}
              maxLength={120}
              data-testid="realtor-editor-tagline"
              className="font-body italic"
            />
            <p className="mt-1 text-xs text-gray-500">
              Shows in gold italic under your name — {120 - tagline.length} characters left.
            </p>
          </div>

          <div>
            <label className="mb-1 block font-body text-sm font-medium text-tbc-navy">
              Specialties (up to 3)
            </label>
            <div className="space-y-2">
              {specialties.map((val, idx) => (
                <div key={`spec-${idx}`} className="flex items-center gap-2">
                  <span className="w-5 flex-none text-center text-xs font-medium text-gray-400">
                    {idx + 1}
                  </span>
                  <Input
                    value={val}
                    onChange={(e) => updateSpecialty(idx, e.target.value)}
                    placeholder={
                      [
                        "Residential Sales",
                        "Investment Properties",
                        "Client-Focused Results",
                      ][idx]
                    }
                    maxLength={40}
                    data-testid={`realtor-editor-specialty-${idx}`}
                  />
                </div>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Three short labels that appear as gold-icon chips at the bottom of your hero.
            </p>
          </div>
        </div>

        {/* RIGHT column — hero portrait */}
        <div>
          <label className="mb-1 block font-body text-sm font-medium text-tbc-navy">
            Hero portrait
          </label>
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-tbc-navy">
            {imageUrl ? (
              <img
                src={resolveAssetUrl(imageUrl)}
                alt="Realtor hero preview"
                className="h-64 w-full object-cover"
                data-testid="realtor-editor-preview"
              />
            ) : (
              <div className="flex h-64 items-center justify-center text-sm text-white/60">
                No hero portrait yet
              </div>
            )}
            {imageUrl && (
              <button
                type="button"
                onClick={() => setImageUrl("")}
                className="absolute right-2 top-2 rounded-full bg-white/95 p-1.5 text-tbc-navy shadow hover:bg-white"
                title="Remove photo"
                data-testid="realtor-editor-remove-photo"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <input
            id="realtor-hero-file"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => uploadPortrait(e.target.files?.[0])}
          />
          <label
            htmlFor="realtor-hero-file"
            className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-full border border-tbc-navy/30 bg-white px-4 py-2 text-sm font-body font-medium text-tbc-navy hover:border-tbc-navy hover:shadow-sm"
            data-testid="realtor-editor-upload-btn"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : imageUrl ? (
              <Upload className="h-4 w-4" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {uploading ? "Uploading…" : imageUrl ? "Replace photo" : "Upload hero photo"}
          </label>
          <p className="mt-2 text-xs text-gray-500">
            Portrait or 3:4 crop works best. You + a property backdrop looks great — under 5 MB.
          </p>
        </div>
      </div>
    </section>
  );
}
